import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { Card, CardHeader, CardTitle, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import AutoFillBookmarklet from './AutoFillBookmarklet';
import PaginationWrapper from './PaginationWrapper';
import { 
  KeyRound, 
  Plus, 
  LogOut, 
  ExternalLink, 
  Edit, 
  Trash2, 
  Globe,
  User,
  Copy,
  Zap,
  Shield,
  Activity
} from 'lucide-react';

const Dashboard = () => {
  const [companies, setCompanies] = useState([]);
  const [pagination, setPagination] = useState({
    total: 0,
    skip: 0,
    limit: 20,
    has_more: false
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [showBookmarklet, setShowBookmarklet] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    fetchCompanies();
  }, [currentPage]);

  const formatUrl = (url) => {
    if (!url.startsWith('http://') && !url.startsWith('https://')) {
      return `https://${url}`;
    }
    return url;
  };

  const fetchCompanies = async () => {
    try {
      const skip = (currentPage - 1) * 20;
      const response = await axios.get(`/companies?skip=${skip}&limit=20`);
      
      if (response.data.companies) {
        setCompanies(response.data.companies);
        setPagination({
          total: response.data.total,
          skip: response.data.skip,
          limit: response.data.limit,
          has_more: response.data.has_more
        });
      } else {
        // Fallback for old API response format
        setCompanies(response.data);
      }
    } catch (error) {
      toast.error('Error loading companies');
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    setLoading(true);
  };

  const handleDeleteCompany = async (companyId) => {
    if (!window.confirm('Are you sure you want to delete this company?')) {
      return;
    }

    try {
      await axios.delete(`/companies/${companyId}`);
      setCompanies(companies.filter(company => company.id !== companyId));
      toast.success('Company deleted successfully');
    } catch (error) {
      toast.error('Error deleting company');
    }
  };

  const handleCopyCredentials = async (company) => {
    const credentialsText = `Usuario: ${company.username}\nContraseña: ${company.password}`;
    
    // Log copy credentials activity
    await logActivity(company.id, 'copy_credentials', `Copied credentials for ${company.name}`);
    
    if (navigator.clipboard) {
      try {
        await navigator.clipboard.writeText(credentialsText);
        toast.success('Credentials copied', {
          description: `User and password for ${company.name} copied to clipboard`,
          duration: 3000,
        });
      } catch (error) {
        toast.error('Copy error', {
          description: 'Could not copy credentials to clipboard',
          duration: 3000,
        });
      }
    } else {
      toast.info('Credentials', {
        description: `User: ${company.username} | Password: ${company.password}`,
        duration: 6000,
      });
    }
  };

  // Removed handlePracticeFusionLogin - extension handles all auto-filling now

  const handleAutoLogin = async (company) => {
    const url = formatUrl(company.login_url);
    
    // Log auto-login activity
    await logActivity(company.id, 'auto_login', `Auto-login attempted for ${company.name} (${url})`);
    
    // Copy credentials to clipboard silently
    const credentialsText = `Usuario: ${company.username}\nContraseña: ${company.password}`;
    
    if (navigator.clipboard) {
      navigator.clipboard.writeText(credentialsText).catch(() => {
        // Silent fallback - no notification needed
      });
    }
    
    // Open in new tab - extension will handle auto-filling
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  // Removed attemptAutoFill function - not needed for tab-based approach

  const handleShowBookmarklet = (company) => {
    setSelectedCompany(company);
    setShowBookmarklet(true);
  };

  // Activity logging function
  const logActivity = async (companyId, action, details) => {
    try {
      await axios.post('/activities/log', {
        company_id: companyId,
        action: action,
        details: details
      });
    } catch (error) {
      console.error('Failed to log activity:', error);
      // Don't show error to user, just log silently
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-900"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-40 backdrop-blur-sm bg-white/95">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-4">
              <div className="bg-gradient-to-r from-blue-900 to-blue-700 p-2 rounded-lg shadow-lg">
                <KeyRound className="h-6 w-6 text-white" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-gray-900">APT-AccessHub</h1>
                <p className="text-xs text-gray-500">Control Panel</p>
              </div>
            </div>
            
            <div className="flex items-center space-x-4">
              <Link to="/activities">
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-full"
                  title="Activity Logs"
                >
                  <Activity className="h-4 w-4" />
                </Button>
              </Link>
              <Link to="/profile">
                <div className="hidden md:flex items-center space-x-3 px-3 py-2 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer">
                  <User className="h-4 w-4 text-gray-500" />
                  <div className="text-right">
                    <p className="text-sm font-medium text-gray-900">{user?.full_name}</p>
                    <p className="text-xs text-gray-500">{user?.email}</p>
                  </div>
                </div>
              </Link>
              <Button
                onClick={logout}
                variant="ghost"
                size="sm"
                className="text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-full"
                title="Sign out"
              >
                <LogOut className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Welcome Section */}
        <div className="mb-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-2">
            Welcome, {user?.full_name}!
          </h2>
          <p className="text-gray-600">
            Manage all your corporate accounts from one secure place
          </p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
          <Card className="bg-gradient-to-r from-blue-500 to-blue-600 text-white card-hover">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium opacity-90 flex items-center">
                <KeyRound className="h-4 w-4 mr-2" />
                Total Companies
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold">{companies.length}</div>
              <p className="text-blue-100 text-sm mt-1">Registered accounts</p>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-r from-orange-500 to-orange-600 text-white card-hover">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium opacity-90 flex items-center">
                <Zap className="h-4 w-4 mr-2" />
                Quick Access
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold">1-Click</div>
              <p className="text-orange-100 text-sm mt-1">Smart auto-login</p>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-r from-green-500 to-green-600 text-white card-hover">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium opacity-90 flex items-center">
                <Shield className="h-4 w-4 mr-2" />
                Security
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold">AES-256</div>
              <p className="text-green-100 text-sm mt-1">Maximum encryption</p>
            </CardContent>
          </Card>
        </div>

        {/* Browser Extension Card */}
        <Card className="mb-8 border-2 border-purple-200 bg-gradient-to-r from-purple-50 to-indigo-50">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <div className="bg-purple-100 p-3 rounded-full">
                  <div className="text-2xl">🧩</div>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Browser Extension Available!</h3>
                  <p className="text-gray-600 text-sm mt-1">
                    Install our browser extension for seamless auto-login on Practice Fusion and other healthcare platforms
                  </p>
                  <div className="flex items-center space-x-4 mt-2">
                    <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded-full">✅ Practice Fusion Support</span>
                    <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full">🔐 Secure Integration</span>
                    <span className="text-xs bg-purple-100 text-purple-800 px-2 py-1 rounded-full">⚡ Instant Fill</span>
                  </div>
                </div>
              </div>
              <div className="flex flex-col space-y-2">
                <Button
                  onClick={async () => {
                    try {
                      // Try API endpoint first (preferred method)
                      const backendUrl = process.env.REACT_APP_BACKEND_URL || window.location.origin;
                      const apiResponse = await fetch(`${backendUrl}/api/extension/download`, {
                        headers: {
                          'Authorization': `Bearer ${localStorage.getItem('token')}`
                        }
                      });
                      
                      let downloadSuccess = false;
                      
                      if (apiResponse.ok) {
                        // API endpoint works - use it
                        const blob = await apiResponse.blob();
                        const url = window.URL.createObjectURL(blob);
                        const link = document.createElement('a');
                        link.href = url;
                        link.download = 'apt-accesshub-extension.zip';
                        document.body.appendChild(link);
                        link.click();
                        document.body.removeChild(link);
                        window.URL.revokeObjectURL(url);
                        downloadSuccess = true;
                      } else if (apiResponse.status === 404) {
                        // API endpoint doesn't exist - try fallback to static file
                        try {
                          const staticResponse = await fetch('/apt-accesshub-extension.zip');
                          if (staticResponse.ok && staticResponse.headers.get('content-type')?.includes('zip')) {
                            // Static file exists and is a valid zip
                            const blob = await staticResponse.blob();
                            const url = window.URL.createObjectURL(blob);
                            const link = document.createElement('a');
                            link.href = url;
                            link.download = 'apt-accesshub-extension.zip';
                            document.body.appendChild(link);
                            link.click();
                            document.body.removeChild(link);
                            window.URL.revokeObjectURL(url);
                            downloadSuccess = true;
                          } else {
                            throw new Error('Static zip file not available or invalid');
                          }
                        } catch (staticError) {
                          console.warn('Both API and static fallback failed:', staticError);
                          throw new Error('Extension download not available');
                        }
                      } else {
                        throw new Error(`API request failed with status: ${apiResponse.status}`);
                      }
                      
                      // Show success message if download worked
                      if (downloadSuccess) {
                        setTimeout(() => {
                          toast.success('Extension Downloaded!', {
                            description: 'Extract the ZIP file and follow these steps: 1) Open chrome://extensions/ 2) Enable Developer Mode 3) Click "Load unpacked" 4) Select the extracted folder',
                            duration: 10000,
                            action: {
                              label: 'Full Instructions',
                              onClick: () => window.open('/browser-extension/install-extension.html', '_blank')
                            }
                          });
                        }, 500);
                      }
                      
                    } catch (error) {
                      console.error('Extension download error:', error);
                      toast.error('Download failed', {
                        description: 'Extension download is temporarily unavailable. Please contact support for manual installation files.'
                      });
                    }
                  }}
                  className="bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 flex items-center space-x-2"
                >
                  <span>📥</span>
                  <span>Download Extension</span>
                </Button>
                <p className="text-xs text-gray-500 text-center">Works with Chrome & Edge</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Actions Bar */}
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-8 gap-4">
          <div>
            <h3 className="text-2xl font-semibold text-gray-900">
              My Companies
            </h3>
            <p className="text-gray-600 text-sm mt-1">
              {companies.length} {companies.length === 1 ? 'company registered' : 'companies registered'}
            </p>
          </div>
          <div className="flex space-x-3">
            <Link to="/add-company">
              <Button className="btn-primary flex items-center space-x-2 px-6">
                <Plus className="h-4 w-4" />
                <span>Add Company</span>
              </Button>
            </Link>
          </div>
        </div>

        {/* Companies Grid */}
        {companies.length === 0 ? (
          <div className="text-center py-16 animate-fade-in">
            <div className="bg-gradient-to-br from-gray-100 to-gray-200 rounded-full w-24 h-24 flex items-center justify-center mx-auto mb-6">
              <KeyRound className="h-12 w-12 text-gray-400" />
            </div>
            <h3 className="text-2xl font-semibold text-gray-900 mb-3">
              Start your centralized management!
            </h3>
            <p className="text-gray-500 mb-8 max-w-md mx-auto">
              Add your first company to start managing all your access from one secure and efficient place.
            </p>
            <Link to="/add-company">
              <Button className="btn-primary px-8 py-3 text-lg">
                <Plus className="h-5 w-5 mr-2" />
                Add First Company
              </Button>
            </Link>
            
            {/* Feature highlights for empty state */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-12 max-w-4xl mx-auto">
              <div className="text-center p-4">
                <div className="bg-blue-100 rounded-full w-12 h-12 flex items-center justify-center mx-auto mb-3">
                  <Zap className="h-6 w-6 text-blue-600" />
                </div>
                <h4 className="font-semibold text-gray-900 mb-2">Smart Auto-Login</h4>
                <p className="text-sm text-gray-600">Access any platform with one click</p>
              </div>
              <div className="text-center p-4">
                <div className="bg-green-100 rounded-full w-12 h-12 flex items-center justify-center mx-auto mb-3">
                  <Shield className="h-6 w-6 text-green-600" />
                </div>
                <h4 className="font-semibold text-gray-900 mb-2">Maximum Security</h4>
                <p className="text-sm text-gray-600">Credentials encrypted with AES-256</p>
              </div>
              <div className="text-center p-4">
                <div className="bg-orange-100 rounded-full w-12 h-12 flex items-center justify-center mx-auto mb-3">
                  <Globe className="h-6 w-6 text-orange-600" />
                </div>
                <h4 className="font-semibold text-gray-900 mb-2">Universal</h4>
                <p className="text-sm text-gray-600">Compatible with any website</p>
              </div>
            </div>
          </div>
        ) : (
          <>
            <div className="company-grid animate-fade-in">
              {companies.map((company) => (
                <Card key={company.id} className="card hover:shadow-xl transition-all duration-300 bg-white border border-gray-200">
                  <CardHeader className="pb-4">
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex items-start space-x-3 flex-1 min-w-0">
                        <div className="bg-gradient-to-r from-blue-100 to-blue-200 p-3 rounded-xl flex-shrink-0">
                          <Globe className="h-6 w-6 text-blue-600" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <CardTitle className="text-lg font-semibold text-gray-900 truncate mb-1">
                            {company.name}
                          </CardTitle>
                          <p className="text-sm text-gray-500 truncate">
                            {company.login_url}
                          </p>
                        </div>
                      </div>
                      <Badge variant="secondary" className="bg-green-100 text-green-800 border-green-200 flex-shrink-0 self-start">
                        <div className="w-2 h-2 bg-green-500 rounded-full mr-2"></div>
                        Active
                      </Badge>
                    </div>
                  </CardHeader>
                  
                  <CardContent className="pt-0 px-6 pb-6">
                    <div className="mb-4">
                      <p className="text-sm text-gray-600 mb-2">User:</p>
                      <p className="font-medium text-gray-900 truncate bg-gray-50 px-3 py-2 rounded-lg text-sm">
                        {company.username}
                      </p>
                    </div>

                    {/* Primary Action Button */}
                    <div className="mb-4">
                      <div className="space-y-2">
                        <Button
                          onClick={() => handleAutoLogin(company)}
                          className="w-full btn-orange flex items-center justify-center space-x-2 h-11 font-semibold"
                        >
                          <ExternalLink className="h-4 w-4" />
                          <span>Open + JS Fill</span>
                        </Button>
                        <p className="text-xs text-gray-500 text-center">
                          Opens page + provides auto-fill code
                        </p>
                      </div>
                    </div>

                    {/* Secondary Action Buttons */}
                    <div className="grid grid-cols-4 gap-2">
                      <Button
                        onClick={() => handleShowBookmarklet(company)}
                        variant="outline"
                        size="sm"
                        className="btn-secondary h-10 flex items-center justify-center p-0 border-gray-300 hover:border-blue-400 hover:text-blue-600"
                        title="Manual auto-fill"
                      >
                        <Zap className="h-4 w-4" />
                      </Button>
                      
                      <Button
                        onClick={() => handleCopyCredentials(company)}
                        variant="outline"
                        size="sm"
                        className="btn-secondary h-10 flex items-center justify-center p-0 border-gray-300 hover:border-green-400 hover:text-green-600"
                        title="Copy credentials"
                      >
                        <Copy className="h-4 w-4" />
                      </Button>
                      
                      <Button
                        onClick={() => navigate(`/edit-company/${company.id}`)}
                        variant="outline"
                        size="sm"
                        className="btn-secondary h-10 flex items-center justify-center p-0 border-gray-300 hover:border-yellow-400 hover:text-yellow-600"
                        title="Edit company"
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                      
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDeleteCompany(company.id)}
                        className="btn-secondary h-10 flex items-center justify-center p-0 border-gray-300 hover:bg-red-50 hover:border-red-400 hover:text-red-600"
                        title="Delete company"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
            
            {/* Pagination */}
            {pagination.total > 0 && (
              <PaginationWrapper
                currentPage={currentPage}
                totalPages={Math.ceil(pagination.total / pagination.limit)}
                total={pagination.total}
                limit={pagination.limit}
                skip={pagination.skip}
                onPageChange={handlePageChange}
                className="mt-6"
              />
            )}
          </>
        )}
      </main>

      {/* Auto-Fill Bookmarklet Modal */}
      {showBookmarklet && selectedCompany && (
        <AutoFillBookmarklet
          company={selectedCompany}
          onClose={() => {
            setShowBookmarklet(false);
            setSelectedCompany(null);
          }}
        />
      )}
    </div>
  );
};

export default Dashboard;